import {
  Box,
  Button,
  Checkbox,
  Flexbox,
  Icon,
  Modal,
  TextField,
  Typography,
} from '@hiiretail/synergy-ui';
import { Controller } from 'react-hook-form';
import { CreateUserFormState, CreateUserModalComponent } from './CreateUserModal.types';
import { ErrorBox } from '../../../../common/components/ErrorBox/ErrorBox';
import { useCreateUserForm } from '../../hooks/useCreateUserForm';
import { useIntl } from 'react-intl';
import { useState } from 'react';
import { IdpUserType } from '../../../../api-client';

export const CreateUserModal: CreateUserModalComponent = ({
  onClose,
  isOpen,
  createUser,
  isCreating,
  error,
}) => {
  const intl = useIntl();
  const [isPasswordVisible, setIsPasswordVisible] = useState(true);

  const { control, handleSubmit, formState, setError } = useCreateUserForm(intl);

  const onFormSubmit = (formData: CreateUserFormState) => {
    if (!formData.autoSendPass) {
      if (formData.password && !formData.confirmPassword) {
        setError('confirmPassword', {
          message: 'Password must be confirmed',
        });
      }
    } else {
      delete formData.password;
    }
    delete formData.autoSendPass;
    delete formData.confirmPassword;
    createUser({ ...formData, scope: IdpUserType.Staff });
  };

  const toggleIsPasswordVisible = () => setIsPasswordVisible((val) => !val);

  return (
    <Modal
      data-testid="create-user-modal"
      title="Create User"
      onClose={onClose}
      open={isOpen}
      footer={
        <Flexbox container justifyContent="flex-end">
          <Button
            type="submit"
            onClick={handleSubmit(onFormSubmit)}
            disabled={!formState.isValid || isCreating}
          >
            {isCreating ? 'Creating ...' : 'Create'}
          </Button>
        </Flexbox>
      }
    >
      <ErrorBox error={error} />
      <Flexbox container gutter={4} mb={4} alignItems="center">
        <Flexbox item>
          <Icon.Info size="lg" color={() => '#6E33A9'} />
        </Flexbox>
        <Flexbox item container>
          <Typography variant="body2">
            Create a standalone user.
            <br />
            Standalone users are created <strong>within Hii Retail system.</strong>
          </Typography>
        </Flexbox>
      </Flexbox>
      <form>
        <Box mb={2}>
          <Controller
            control={control}
            name="name"
            render={({ field: { ref, value, onChange } }) => (
              <TextField
                tabIndex={0}
                fullWidth
                autoComplete="name"
                label="Name"
                type="text"
                name="new-user-name"
                value={value}
                ref={ref}
                onChange={onChange}
                hint="Unique name of the user"
                error={formState.errors?.name?.message}
              />
            )}
          />
        </Box>

        <Box mb={2}>
          <Controller
            name="type"
            defaultValue="Standalone"
            control={control}
            render={() => <></>}
          />
        </Box>

        <Box mb={2}>
          <Controller
            name="email"
            defaultValue=""
            control={control}
            render={({ field: { ref, value, onChange } }) => (
              <TextField
                tabIndex={0}
                fullWidth
                autoComplete="email"
                label="Email"
                name="email"
                type="email"
                hint="User's email address"
                ref={ref}
                value={value}
                onChange={onChange}
                error={formState.errors?.email?.message}
              />
            )}
          />
        </Box>
        <Controller
          name="autoSendPass"
          defaultValue={false}
          control={control}
          render={({ field: { value, onChange } }) => (
            <Checkbox
              name="showPassword"
              label="Automatically send password reset link to email address"
              checked={value}
              onChange={(e) => {
                toggleIsPasswordVisible();
                onChange(e);
              }}
            />
          )}
        />
        {isPasswordVisible && (
          <>
            <Box mb={2}>
              <Controller
                name="password"
                defaultValue=""
                control={control}
                render={({ field: { ref, value, onChange } }) => (
                  <TextField
                    tabIndex={0}
                    fullWidth
                    autoComplete="new-password"
                    label="Password"
                    name="password"
                    type="password"
                    ref={ref}
                    value={value}
                    onChange={onChange}
                    //@ts-ignore
                    hint={
                      <span>
                        Must be at least 8 characters long
                        <br />
                        including 1 uppercase letter, 1 special character and 1 number
                      </span>
                    }
                    error={formState.errors?.password?.message}
                  />
                )}
              />
            </Box>
            <Box mb={2}>
              <Controller
                name="confirmPassword"
                defaultValue=""
                control={control}
                render={({ field: { ref, value, onChange } }) => (
                  <TextField
                    tabIndex={0}
                    fullWidth
                    autoComplete="new-password"
                    label="Confirm password"
                    name="confirmPassword"
                    type="password"
                    ref={ref}
                    value={value}
                    onChange={onChange}
                    error={formState.errors?.confirmPassword?.message}
                  />
                )}
              />
            </Box>
          </>
        )}
      </form>
    </Modal>
  );
};
